import React, { useState, useEffect, useCallback } from "react";
import Customer from "./CustomersTableItem";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { FaCloudArrowUp, FaClockRotateLeft, FaDownload, FaMagnifyingGlass, FaCrown } from "react-icons/fa6";
import ModalBasic from "@/components/ModalBasic.jsx";
import FileUpload from "@/components/FileUpload.jsx";
import Papa from "papaparse";
import { notifyCsvUpload, getCrmUploadJobs } from "@/api/crm";
import { getFileUploadPresignedUrl } from "@/api/upload";
import { uploadFileToS3 } from "@/api";
import Badge from "@/components/Badges.jsx";
import CRMAccessBlocker from "../CRMAccessBlocker";
import CanView from "@/components/CanView.jsx";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";

function CustomersTable({ selectedItems, customers, totalRecords, onSearch }) {
  if (!customers) {
    return;
  }
  const [selectAll, setSelectAll] = useState(false);
  const [isCheck, setIsCheck] = useState([]);
  const [list, setList] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState(1);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRowCount, setCsvRowCount] = useState(0);
  const systemFields = [
    "firstname",
    "lastname",
    "email",
    "phone",
    "birthday",
    "fullname",
  ];
  const systemFieldFriendly = {
    firstname: "First Name",
    lastname: "Last Name",
    email: "Email",
    phone: "Phone Number",
    birthday: "Birthday",
    fullname: "Full Name"
  };
  const [fieldMapping, setFieldMapping] = useState({});
  const [mappingError, setMappingError] = useState("");
  const [duplicateFields, setDuplicateFields] = useState([]);
  const [uploadHistoryModalOpen, setUploadHistoryModalOpen] = useState(false);
  const EMAIL_FIELD = "email";
  const [uploadJobs, setUploadJobs] = useState([]);
  const [uploadJobsLoading, setUploadJobsLoading] = useState(false);
  const [uploadJobsError, setUploadJobsError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [globalSearch, setGlobalSearch] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    onSearch(debouncedSearchQuery, globalSearch);
  }, [debouncedSearchQuery, globalSearch]);



  useEffect(() => {
    setList(customers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  useEffect(() => {
    if (uploadModalOpen) {
      setMappingError("");
      setDuplicateFields([]);
      setFieldMapping({});
      setCsvHeaders([]);
      setCsvRowCount(0);
      setUploadStep(1);
    }
  }, [uploadModalOpen]);

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setIsCheck(list.map((li) => li.id));
    if (selectAll) {
      setIsCheck([]);
    }
  };

  const handleClick = (e) => {
    const { id, checked } = e.target;
    setSelectAll(false);
    setIsCheck([...isCheck, id]);
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id));
    }
  };

  useEffect(() => {
    selectedItems?.(isCheck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheck]);

  const handleFileChange = (files) => {
    setUploadedFiles(files);
    if (files && files[0]) {
      Papa.parse(files[0], {
        complete: function (results) {
          const headers = results.data[0] || [];
          setCsvHeaders(headers);
          // Initialize mapping with empty values
          const initialMapping = {};
          headers.forEach((h) => {
            initialMapping[h] = "";
          });
          setFieldMapping(initialMapping);
          // Count data rows (exclude header, ignore empty rows)
          const dataRows = results.data
            .slice(1)
            .filter(
              (row) =>
                Array.isArray(row) &&
                row.some(
                  (cell) =>
                    cell !== undefined &&
                    cell !== null &&
                    String(cell).trim() !== ""
                )
            );
          setCsvRowCount(dataRows.length);
        },
        skipEmptyLines: true,
      });
    } else {
      setCsvHeaders([]);
      setFieldMapping({});
      setCsvRowCount(0);
    }
  };

  const handleMappingChange = (csvField, value) => {
    setFieldMapping((prev) => ({ ...prev, [csvField]: value }));
  };

  const handleCancel = () => {
    setUploadModalOpen(false);
    setUploadStep(1);
    setUploadedFiles([]);
    setFieldMapping({});
    setCsvHeaders([]);
  };

  const handleImport = async () => {
    // Check for duplicate mapped values (excluding empty)
    const values = Object.values(fieldMapping).filter(Boolean);
    const duplicates = values.filter(
      (item, idx) => values.indexOf(item) !== idx
    );
    if (duplicates.length > 0) {
      setMappingError("Each Saucey Direct field can only be mapped once.");
      setDuplicateFields(duplicates);
      return;
    }
    // Check if Email is mapped
    const hasEmail = Object.values(fieldMapping).includes(EMAIL_FIELD);
    if (!hasEmail) {
      setMappingError("You must map a field to Email.");
      setDuplicateFields([]);
      return;
    }

    // Check name field mapping - must have either [firstname, lastname] or [fullname], but not both
    const mappedFields = Object.values(fieldMapping).filter(Boolean);
    const hasFirstName = mappedFields.includes("firstname");
    const hasLastName = mappedFields.includes("lastname");
    const hasFullName = mappedFields.includes("fullname");

    const hasFirstLastName = hasFirstName && hasLastName;
    const hasFullNameOnly = hasFullName && !hasFirstName && !hasLastName;

    if (!hasFirstLastName && !hasFullNameOnly) {
      setMappingError("You must map either [First Name, Last Name] or [Full Name], but not both.");
      setDuplicateFields([]);
      return;
    }

    if (hasFirstLastName && hasFullName) {
      setMappingError("You cannot map both [First Name, Last Name] and [Full Name]. Choose one approach.");
      setDuplicateFields([]);
      return;
    }
    setMappingError("");
    setDuplicateFields([]);

    // Import/upload logic
    try {
      setUploadLoading(true);
      if (!uploadedFiles || !uploadedFiles[0])
        throw new Error("No file selected");
      const file = uploadedFiles[0];
      // 1. Get presigned URL
      const { key, presigned_url } = await getFileUploadPresignedUrl("csv");
      // 2. Upload file to S3
      const uploadRes = await uploadFileToS3(presigned_url, file, "text/csv");
      if (!uploadRes.ok) throw new Error("Failed to upload file to S3");
      // 3. Notify backend (send mapping too)
      await notifyCsvUpload({
        key,
        originalFileName: file.name,
        numRows: csvRowCount,
        mapping: fieldMapping,
      });
      setUploadStep(3);
    } catch (err) {
      setMappingError(err.message || "Import failed");
    } finally {
      setUploadLoading(false);
    }
  };

  useEffect(() => {
    if (uploadHistoryModalOpen) {
      setUploadJobsLoading(true);
      setUploadJobsError("");
      getCrmUploadJobs()
        .then((res) => setUploadJobs(res.jobs || []))
        .catch((err) => setUploadJobsError("Failed to load upload history"))
        .finally(() => setUploadJobsLoading(false));
    }
  }, [uploadHistoryModalOpen]);

  function getStatusColor(status) {
    switch (status) {
      case "completed":
        return "green";
      case "pending":
        return "blue";
      case "partial":
        return "yellow";
      case "failed":
        return "red";
      default:
        return "gray";
    }
  }

  return (
    <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative">
      <header className="px-5 py-4">
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>
                All People{" "}
                <span className="text-slate-400 dark:text-slate-500 font-medium">
                  {totalRecords}
                </span>
              </span>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaMagnifyingGlass className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name / email / phone / ID"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="form-input w-72 !pl-8 pr-10"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <CanView permissions="saucey_admin">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <ToggleSwitch
                    checked={globalSearch}
                    onChange={setGlobalSearch}
                    label="Global Search"
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Global Search
                  </span>
                  <FaCrown className="w-4 h-4 text-amber-500" />
                </div>
              </div>
              </CanView>
            </div>
            <CanView permissions={"manager"} addons={["crm"]}>
              <div className="flex items-center gap-2">
                <PrimaryButton
                  type="button"
                  onClick={() => {
                    setUploadModalOpen(true);
                  }}
                >
                  <div className="flex items-center">
                    <FaCloudArrowUp className="w-4 h-4 mr-2" />
                    Upload contacts
                  </div>
                </PrimaryButton>
                <button
                  type="button"
                  className="p-2 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                  title="Upload History"
                  onClick={() => setUploadHistoryModalOpen(true)}
                >
                  <FaClockRotateLeft className="w-4 h-4" />
                </button>
              </div>
            </CanView>
          </div>
        </h2>
      </header>
      <div>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="table-auto w-full dark:text-slate-300">
            {/* Table header */}
            <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
                  <div className="flex items-center">
                    <label className="inline-flex">
                      <span className="sr-only">Select all</span>
                      <input
                        className="form-checkbox"
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </label>
                  </div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
                  <span className="sr-only">Favourite</span>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Name</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Email</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Phone</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Orders</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Total spent</div>
                </th>
                {/*<th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">*/}
                {/*  <span className="sr-only ">Menu</span>*/}
                {/*</th>*/}
              </tr>
            </thead>
            {/* Table body */}
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              {list.map((customer) => {
                return (
                  <Customer
                    customer={customer}
                    key={customer.email || customer.mobile}
                    id={customer.email || customer.mobile}
                    image={customer.image}
                    name={`${customer.first_name} ${customer.last_name}`}
                    email={customer.email}
                    location={customer.location}
                    orders={customer.order_count}
                    lastOrder={customer.lastOrder}
                    spent={customer.total_spent}
                    refunds={customer.refunds}
                    fav={customer.fav}
                    handleClick={handleClick}
                    isChecked={isCheck.includes(
                      customer.email || customer.mobile
                    )}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ModalBasic
        id="upload-contacts-modal"
        modalOpen={uploadModalOpen}
        setModalOpen={setUploadModalOpen}
        title="Upload Contacts"
        width="xl"
      >
        {uploadStep === 1 && (
          <div className="flex flex-col gap-6">
            <FileUpload
              accept={[".csv"]}
              multiple={false}
              inputContent="Drag & drop or click to upload a CSV file"
              onChange={handleFileChange}
            />
            <div className="flex justify-end gap-2">
              <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
              <PrimaryButton
                onClick={() => setUploadStep(2)}
                disabled={uploadedFiles.length === 0}
              >
                Next
              </PrimaryButton>
            </div>
          </div>
        )}
        {uploadStep === 2 && (
          <div className="flex flex-col gap-6">
            <div>
              <h3 className="font-semibold mb-2">
                Map CSV Fields to Saucey Direct Fields
              </h3>
              {mappingError && (
                <div className="mb-2 text-red-500 font-medium">
                  {mappingError}
                </div>
              )}
              <div className="space-y-4">
                {csvHeaders.map((csvField) => (
                  <div key={csvField} className="flex items-center gap-4">
                    <div className="w-1/3 font-mono text-slate-700 dark:text-slate-200">
                      {csvField}
                    </div>
                    <select
                      className={`form-select w-2/3 ${
                        (duplicateFields.includes(fieldMapping[csvField]) &&
                          fieldMapping[csvField]) ||
                        (mappingError &&
                          mappingError.includes("Email") &&
                          fieldMapping[csvField] === EMAIL_FIELD)
                          ? "border-red-500"
                          : ""
                      }`}
                      value={fieldMapping[csvField] || ""}
                      onChange={(e) =>
                        handleMappingChange(csvField, e.target.value)
                      }
                    >
                      <option value="">-- Select Saucey Direct Field --</option>
                      {systemFields.map((field) => (
                        <option key={field} value={field}>
                          {systemFieldFriendly[field]}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
              <PrimaryButton loading={uploadLoading} onClick={handleImport}>
                {csvRowCount > 0 ? `Import ${csvRowCount} records` : "Import"}
              </PrimaryButton>
            </div>
          </div>
        )}
        {uploadStep === 3 && (
          <div className="flex flex-col gap-6 items-center justify-center text-center min-h-[200px]">
            <div className="text-2xl font-bold text-green-600">
              Upload Queued!
            </div>
            <div className="text-slate-700 dark:text-slate-200">
              Your upload has been queued for processing.
              <br />
              You can check the status in the upload history.
            </div>
            <PrimaryButton onClick={() => setUploadModalOpen(false)}>
              Close
            </PrimaryButton>
          </div>
        )}
        <CRMAccessBlocker
          dismissAction={() => {
            setUploadModalOpen(false);
          }}
        />
      </ModalBasic>
      <ModalBasic
        id="upload-history-modal"
        modalOpen={uploadHistoryModalOpen}
        setModalOpen={setUploadHistoryModalOpen}
        title="Upload History"
        width="5xl"
      >
        <div className="overflow-x-auto">
          {uploadJobsLoading ? (
            <div className="p-4 text-center">Loading...</div>
          ) : uploadJobsError ? (
            <div className="p-4 text-center text-red-600">
              {uploadJobsError}
            </div>
          ) : (
            <table className="min-w-full text-sm text-left">
              <thead className="bg-slate-100 dark:bg-theme-surface-accent">
                <tr>
                  <th className="px-4 py-2 font-semibold">Upload Time</th>
                  <th className="px-4 py-2 font-semibold">Processed Time</th>
                  <th className="px-4 py-2 font-semibold">File Name</th>
                  <th className="px-4 py-2 font-semibold">Rows in File</th>
                  <th className="px-4 py-2 font-semibold">Rows Imported</th>
                  <th className="px-4 py-2 font-semibold">Failed Rows</th>
                  <th className="px-4 py-2 font-semibold">Duplicated Rows</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {uploadJobs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-4 text-center">
                      No uploads found
                    </td>
                  </tr>
                ) : (
                  uploadJobs.map((job) => (
                    <tr
                      key={job.id}
                      className="border-b border-slate-200 dark:border-slate-700"
                    >
                      <td className="px-4 py-2 whitespace-nowrap">
                        {new Date(job.date_created).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {job.last_updated
                          ? new Date(job.last_updated).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {job.original_file_name}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {job.num_rows}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {job.rows_imported}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {typeof job.rows_failed === "number"
                          ? job.rows_failed
                          : "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {typeof job.rows_duplicated === "number"
                          ? job.rows_duplicated
                          : "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <Badge color={getStatusColor(job.status)}>
                          <span className="capitalize">{job.status}</span>
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        <CRMAccessBlocker
          dismissAction={() => setUploadHistoryModalOpen(false)}
        />
      </ModalBasic>
    </div>
  );
}

export default CustomersTable;
